import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, CalendarCheck, Sun, Moon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { getSupervisorAttendance, setSupervisorAttendance } from '@/services/payrollApi';

// Build inclusive list of YYYY-MM-DD strings between two dates (cap to 62 days).
const buildDates = (from, to) => {
  if (!from || !to) return [];
  const out = [];
  const d = new Date(from + 'T00:00:00');
  const end = new Date(to + 'T00:00:00');
  let guard = 0;
  while (d <= end && guard < 62) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
    guard += 1;
  }
  return out;
};

const fmtCol = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch { return iso; }
};

const SupervisorAttendance = ({ dateFrom, dateTo }) => {
  const [supervisors, setSupervisors] = useState([]);
  const [attendance, setAttendance] = useState({});   // "sid|date" -> {day_present, night_present}
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);          // "sid|date|shift"

  const dates = useMemo(() => buildDates(dateFrom, dateTo), [dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getSupervisorAttendance({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined });
      setSupervisors(d.supervisors || []);
      setAttendance(d.attendance || {});
      setIsAdmin(!!d.is_admin);
    } catch (e) { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (sid, date, shift) => {
    if (!isAdmin) return;
    const key = `${sid}|${date}`;
    const cur = attendance[key] || { day_present: false, night_present: false };
    const next = { ...cur };
    if (shift === 'day') next.day_present = !cur.day_present;
    else next.night_present = !cur.night_present;
    setSaving(`${key}|${shift}`);
    // optimistic
    setAttendance((prev) => ({ ...prev, [key]: next }));
    try {
      await setSupervisorAttendance({
        supervisorStaffId: sid, workDate: date,
        dayPresent: next.day_present, nightPresent: next.night_present,
      });
    } catch (e) {
      setAttendance((prev) => ({ ...prev, [key]: cur }));  // revert
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(null); }
  };

  const Pill = ({ sid, date, shift }) => {
    const key = `${sid}|${date}`;
    const on = shift === 'day' ? attendance[key]?.day_present : attendance[key]?.night_present;
    const busy = saving === `${key}|${shift}`;
    const Icon = shift === 'day' ? Sun : Moon;
    return (
      <button
        type="button"
        onClick={() => toggle(sid, date, shift)}
        disabled={!isAdmin || busy}
        title={`${shift === 'day' ? 'Day' : 'Night'} shift — ${on ? 'Present' : 'Absent'}`}
        data-testid={`att-${sid}-${date}-${shift}`}
        className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold transition-colors ${
          on ? (shift === 'day' ? 'bg-amber-400 text-white' : 'bg-indigo-600 text-white')
             : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
        } ${!isAdmin ? 'cursor-default' : ''}`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      </button>
    );
  };

  return (
    <Card data-testid="supervisor-attendance">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarCheck className="w-5 h-5" />Supervisor Attendance</CardTitle>
        <CardDescription>
          Mark each supervisor <span className="font-medium">present</span> for the
          <Sun className="w-3.5 h-3.5 inline mx-1 text-amber-500" />day and
          <Moon className="w-3.5 h-3.5 inline mx-1 text-indigo-600" />night shift, per day.
          A supervisor is credited only for shifts he's marked present — unmarked = absent.
          Grid follows the date range selected above.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center text-[#5A5F6E]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Loading…</div>
        ) : supervisors.length === 0 ? (
          <div className="py-10 text-center text-[#5A5F6E]" data-testid="supervisor-attendance-empty">
            No supervisors assigned yet. Assign supervisors in the Settings tab first.
          </div>
        ) : dates.length === 0 ? (
          <div className="py-10 text-center text-[#5A5F6E]">Pick a date range above to mark attendance.</div>
        ) : (
          <div className="overflow-x-auto">
            {!isAdmin && <p className="text-xs text-[#B45309] mb-2">View only — admin access required to edit.</p>}
            <table className="text-sm border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-[#1A1F2E] text-white text-left px-3 py-2 min-w-[200px]">Supervisor</th>
                  {dates.map((d) => (
                    <th key={d} className="bg-[#1A1F2E] text-white px-2 py-2 text-center whitespace-nowrap min-w-[64px]">{fmtCol(d)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {supervisors.map((s) => (
                  <tr key={s.staff_id} className="border-b" data-testid={`att-row-${s.staff_id}`}>
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 border-r">
                      <div className="font-medium text-[#1A1F2E] truncate max-w-[180px]">{s.staff_name}</div>
                      <div className="text-[11px] text-[#5A5F6E]">{s.staff_id}</div>
                    </td>
                    {dates.map((d) => (
                      <td key={d} className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Pill sid={s.staff_id} date={d} shift="day" />
                          <Pill sid={s.staff_id} date={d} shift="night" />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SupervisorAttendance;
